import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
  toJS,
} from 'mobx'
import { debounce, camelCase, toUpper } from 'lodash'
import {
  createLiveReportSnapshot,
  fetchLiveReportSnapshots,
  fetchLiveReportStructureById,
  fetchReportById,
} from '@services/api/reportAPI'
import {
  fetchLiveReportFilters,
  fetchReportFilters,
} from '@services/api/filterAPI'
import {
  fetchUserPreferences,
  postUserLogin,
  putUserPreferences,
} from '@services/api/userAPI'
import rootStore from './RootStore'
import { ReportComponentType } from '../constants'
import { matchesFilters, reportViewKey } from '@utils/ReportHelper'
import { apiErrorKindOf } from '@services/apiErrors'

class ReportStore {
  loadingUserPreferences = false
  userPreferences = []
  errorSaveUserPreferences = null

  currentReport = {}
  reportId = undefined
  loadingReport = false

  /**
   * Why the current report view failed, as `{ kind, key }`, or null. `key` is the
   * `reportId:snapshotId` the failure belongs to, so switching reports clears it and so the
   * report page can tell "this view failed" from "a different view failed earlier".
   *
   * Declared here rather than assigned on demand: makeAutoObservable only annotates the fields
   * that exist at construction, and an unobserved field cannot re-render anything — which is why
   * the older `error` field was set on every failure and read by nobody.
   */
  reportError = null

  currentSnapshotId = undefined
  snapshots = []
  loadingSnapshots = false
  loadedKey = undefined

  loading = false
  list = {}
  filter = {}
  period = {}
  viewType = { value: '2x2' }
  reportSearchText = ''

  /**
   * Bumped by every search reset, so the search box has something that always changes to follow.
   * `reportSearchText` cannot carry a reset on its own: a reset that only cancels a pending
   * keystroke leaves it at `''`, MobX does not notify on an unchanged value, and the box is
   * uncontrolled — it would keep showing a term the store has already dropped.
   */
  searchResetCount = 0

  currentChartId = undefined
  currentSectionId = undefined

  downloadSelection = []

  /**
   * All filters available in the current Report
   */
  loadingFilters = true
  filtersAvailable = {}

  /**
   * Stores selected filters' values
   * It must reset values by reaching a different section;
   */
  filterOptionsSelected = observable.map()

  constructor() {
    makeAutoObservable(
      this,
      {
        userPreferences: observable,
        currentChartId: observable,
        reportSearchText: observable,
        setReportListIds: action,
        toggleReportList: action,
        // Left unannotated on purpose: `action` — and makeAutoObservable's own default for a
        // function field — replaces it with a wrapper that does not carry lodash's `.cancel`, which
        // `resetReportSearchText` needs. Nothing is lost; the mutation is already in `action()`.
        setSearch: false,
        reportOrdered: computed,
        hasReportData: computed,
        filterOptionsSelected: observable,
      },
      { autoBind: true }
    )
  }

  storeReportById = async ({ reportId, snapshotId }) => {
    try {
      const {
        productsStore: { currentProduct },
        planStore: { currentPlan },
      } = rootStore
      runInAction(() => {
        this.loadingReport = true
        this.reportError = null
        this.reportId = camelCase(reportId)
        this.currentSnapshotId = snapshotId
        // A term typed on the view being left must not filter this one.
        this.resetReportSearchText()
        // Nor may charts ticked for export on it survive: the ids belong to a report the reader
        // has left, so an export started from the new view would report on charts that are not in
        // it. Unlike the filter selection, this reset belongs in the action that *starts* the load
        // rather than on the success path — a stale tick must not survive into the new view even
        // if the load fails.
        this.resetDownloadSelection()
        rootStore.chartStore?.setCharts?.({})
      })
      // Parallelize API calls to reduce waterfall latency
      let report, userPreferences, filterCatalogue
      if (currentProduct === 'pa' && toUpper(reportId) === 'LIVEREPORT') {
        // Validate currentPlan exists before making API calls
        if (!currentPlan?.planId) {
          throw new Error('Current plan is required for LiveReport')
        }

        const [reportData, userPrefs, filters] = await Promise.all([
          fetchLiveReportStructureById({
            planId: currentPlan.planId,
            productId: currentProduct,
            snapshotId,
          }),
          fetchUserPreferences(),
          fetchLiveReportFilters({ planId: currentPlan.planId, productId: currentProduct, snapshotId })
        ])
        report = reportData
        userPreferences = userPrefs
        filterCatalogue = filters
      } else {
        const [reportData, userPrefs] = await Promise.all([
          fetchReportById({ reportId }),
          fetchUserPreferences()
        ])
        report = reportData
        userPreferences = userPrefs

        // Fetch filters after report is available
        filterCatalogue = await fetchReportFilters(camelCase(reportId))
      }

      runInAction(() => {
        // Set user preferences and current report first
        this.userPreferences = userPreferences
        this.currentReport = report
        this.currentSectionId = report.components[0]?.id
        this.loadedKey = reportViewKey({ reportId, snapshotId })

        // Setup journey list synchronously (prefs and report already available)
        this._setupJourneyListSync()

        // Clear and repopulate the filter selection as one step. `_processFiltersSync` only ever
        // adds the new report's defaults, so an entry for a filter this report does not have would
        // survive and leave SectionListItem's "every selected filter matches" test unsatisfiable
        // for every chart, pruning every section away. Clearing when the load *started* instead
        // left a failed load holding an empty map for the rest of the session: only this path
        // repopulates it, and a reader who returns to the view that did load finds `loadedKey`
        // already matching, so nothing refetches and nothing restores the selection.
        this.invalidateFilterOptionsSelected()
        this._processFiltersSync(filterCatalogue)
      })
    } catch (e) {
      runInAction(() => {
        this.reportError = {
          kind: apiErrorKindOf(e),
          key: reportViewKey({ reportId, snapshotId }),
        }
      })
    } finally {
      runInAction(() => {
        this.loadingReport = false
      })
    }
  }

  setCurrentSnapshotId = snapshotId => {
    this.currentSnapshotId = snapshotId
  }

  fetchSnapshots = async () => {
    const {
      productsStore: { currentProduct },
      planStore: { currentPlan },
    } = rootStore
    if (currentProduct !== 'pa' || !currentPlan?.planId) return
    try {
      runInAction(() => {
        this.loadingSnapshots = true
      })
      const snapshots = await fetchLiveReportSnapshots({
        productId: currentProduct,
        planId: currentPlan.planId,
      })
      runInAction(() => {
        this.snapshots = snapshots
      })
    } catch (e) {
      runInAction(() => {
        this.error = 'An error happened while fetching snapshots'
      })
    } finally {
      runInAction(() => {
        this.loadingSnapshots = false
      })
    }
  }

  saveSnapshot = async ({ name, asOf, dataYear }) => {
    const {
      productsStore: { currentProduct },
      planStore: { currentPlan },
    } = rootStore
    if (currentProduct !== 'pa' || !currentPlan?.planId) return undefined
    const snapshotId = await createLiveReportSnapshot({
      productId: currentProduct,
      planId: currentPlan.planId,
      name,
      asOf,
      dataYear,
    })
    await this.fetchSnapshots()
    return snapshotId
  }

  initializeUserPreferences = async () => {
    const userPreferences = await fetchUserPreferences()
    runInAction(() => {
      this.userPreferences = userPreferences
    })
  }

  /**
   * Keep the report root children in order when user preferences are already loaded
   * @private
   */
  _setupJourneyListSync = () => {
    const sectionOrder = this.userPreferences[this.reportId]?.sectionIds || []

    let reportChildren = toJS(this.currentReport.components)
    if (sectionOrder.length > 0) {
      reportChildren = reportChildren.sort(
        (a, b) => sectionOrder.indexOf(a.id) - sectionOrder.indexOf(b.id)
      )
    }

    this.list = {
      tasks: reportChildren,
      columns: {
        'column-1': {
          id: 'column-1',
          title: 'Journeys',
          taskIds: [...reportChildren.map(journey => journey.id)],
        },
      },
      columnOrder: ['column-1'],
    }
  }

  /**
   * Process all available filters for the current report when filters are already fetched
   * @param {Object} fetchedFilterCatalogue - The filter catalogue data
   * @private
   */
  _processFiltersSync = (fetchedFilterCatalogue) => {
    try {
      this.loadingFilters = true

      if (
        !this.currentReport.components ||
        this.currentReport.components.length === 0
      ) {
        // A report with nothing in it has no filters. Returning before these two left the navbar
        // offering the *previous* report's filters over an empty selection, and `loadingFilters`
        // stuck true.
        this.filtersAvailable = {}
        this.loadingFilters = false
        return
      }

      const standardizedFilterCatalogue = fetchedFilterCatalogue.filters.reduce((acc, filter) => {
        acc[filter.id] = {
          id: filter.id,
          name: filter.id,
          nameText: filter.name,
          options: filter.filterValues.map(filterValue => {
            return {
              filterId: filter.id,
              value: filterValue.id,
              text: filterValue.longName === '' ? 'Off' : filterValue.longName,
              default: filterValue.isDefault,
            }
          }),
        }

        return acc
      }, {})

      const getAvailableFilters = component => {
        const uniqueFilterIds = new Set()

        if (component.filters.length > 0) {
          // Filter out invalid filters and collect valid ones in a single pass
          component.filters = component.filters.filter(componentFilter => {
            const filter = standardizedFilterCatalogue[componentFilter.name]
            if (filter) {
              uniqueFilterIds.add(filter.id)
              return true  // keep valid filter
            }
            return false  // remove invalid filter
          })
        }

        return Array.from(uniqueFilterIds).map(filterId => standardizedFilterCatalogue[filterId])
      }

      this.filtersAvailable = this.currentReport.components.reduce((acc, component) => {
        acc[component.id] = getAvailableFilters(component)
        return acc
      }, {})

      // Set current filters values
      const allCurrentFilters = this.allFilters
      for (let filter of allCurrentFilters) {
        let defaultOption = filter.options.find(option => option.default)
        if (!defaultOption) defaultOption = filter.options[0]

        this.setFilterOptionsSelected({
          filterName: filter.name,
          option: {
            id: defaultOption.value,
            value: defaultOption.value,
            label: defaultOption.text,
          },
        })
      }

      this.loadingFilters = false
    } catch (e) {
      console.error(e)
      this.error = 'Filter load error'
      this.loadingFilters = false
    }
  }

  setList = async list => {
    this.list = list
    await this.saveUserPreferences({
      reportId: this.currentReport.id,
      sectionIds: this.list.columns['column-1'].taskIds,
    })
  }

  getTaskIdsForColumn(columnId = 'column-1') {
    return this.list.columns?.[columnId]?.taskIds ?? []
  }

  getUserValidReports(report = []) {
    const { userPreferences } = this
    return report?.filter(r =>
      true  // Utilize userPreferences here to filter the sections for static report if needed in the future
    ) ?? []
  }

  sortReportsByTaskIds(reports = [], taskIds = []) {
    return taskIds.length > 0
      ? [...reports].sort((a, b) => taskIds.indexOf(a.id) - taskIds.indexOf(b.id))
      : reports
  }

  removeEmptyComponents = (report = []) => {
    report = this.removeEmptySubSubSections(report)
    report = this.removeEmptySubSections(report)
    report = this.removeEmptySections(report)
    return report
  }

  removeEmptySubSubSections = (report = []) => {
    const isSubSubSection = subSectionChildren => subSectionChildren.componentType === ReportComponentType.SECTION
    return report.map(section => ({
      ...section,
      children: section.children?.map(subSection => ({
        ...subSection,
        children: subSection.children?.filter(subSectionChildren => {
          return isSubSubSection(subSectionChildren) ? subSectionChildren.children?.some(chart => matchesFilters(chart.filters, this.filterOptionsSelected)) : true
        })
      }))
    }))
  }

  removeEmptySubSections = (report = []) => {
    return report.map(section => ({
      ...section,
      children: section.children?.filter(subSection => subSection.children?.length > 0)
    }))
  }

  removeEmptySections = (report = []) => {
    return report.filter(section => section.children?.length > 0)
  }

  filteredReportsBySearch = (reports, searchText) => {
    if (searchText === '') return reports
    return reports.filter(report => {
      // These ENUMS don't help, really wished type was here instead of componentType
      if (report.componentType !== 1) return false

      if (report?.children?.length > 0) {
        let filteredChildren = this.filteredReportsBySearch(
          report.children,
          searchText
        )
        if (filteredChildren.length > 0) {
          report.children = filteredChildren
          return true
        }
      }

      return report?.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
    })
  }

  get reportOrdered() {
    const searchText = this.reportSearchText
    // Use toJS instead of cloneDeep for better performance with MobX observables
    const clonedReport = toJS(this.list.tasks ?? [])
    const taskIds = this.getTaskIdsForColumn('column-1')

    const validReport = this.getUserValidReports(clonedReport)
    const sortedReport = this.sortReportsByTaskIds(validReport, taskIds)
    const filteredReport = this.filteredReportsBySearch(sortedReport, searchText)
    const cleanedReport = this.removeEmptyComponents(filteredReport)
    return cleanedReport
  }

  get hasReportData() {
    return this.currentReport?.components?.length > 0
  }

  setFilter = filter => {
    this.filter = filter
  }

  setPeriod = period => {
    this.period = period
  }

  setViewType = viewType => {
    this.viewType = viewType
  }

  setDownloadSelection = value => {
    const chart = this.downloadSelection.find(item => item === value)
    if (chart) {
      this.downloadSelection = [
        ...this.downloadSelection.filter(item => item !== value),
      ]
    } else {
      this.downloadSelection.push(value)
    }
  }

  setCurrentChartId = value => {
    this.currentChartId = value
  }

  resetDownloadSelection = () => {
    this.downloadSelection = []
  }

  setReportListIds = async ids => {
    if (!this.userPreferences[this.reportId]) {
      this.userPreferences[this.reportId] = {
        reportListIds: [],
      }
    }
    this.userPreferences[this.reportId].reportListIds = [...ids]
    await this.saveUserPreferences({
      reportId: this.reportId,
      sectionIds: this.list.columns?.['column-1']?.taskIds || [],
      reportListIds: [...ids],
    })
  }

  toggleReportList = async reportId => {
    let reportListIds = [...this.reportListIds]
    if (reportListIds.includes(reportId)) {
      reportListIds = [...reportListIds.filter(id => id !== reportId)]
    } else {
      reportListIds = [...reportListIds, reportId]
    }
    await this.setReportListIds(reportListIds)
  }

  setSearch = debounce(
    action(newSearch => {
      this.reportSearchText = newSearch
    }),
    500
  )

  /**
   * A search belongs to the view it was typed in, so changing view drops it. Nothing else reset it,
   * and a term that outlives its report filters the next one down to nothing.
   *
   * Cancels the pending `setSearch` as well as writing: `setSearch` is debounced 500 ms, so a
   * keystroke made just before the view changed would otherwise land afterwards and put the old
   * report's term back, intermittently.
   */
  resetReportSearchText = () => {
    this.setSearch.cancel()
    this.reportSearchText = ''
    this.searchResetCount += 1
  }

  get sectionTitle() {
    return this.reportOrdered?.find(
      section => section.id === this.currentSectionId
    )?.title
  }

  findParentComponent(componentId, components = this.currentReport.components, parent = null) {
    for (const component of components) {
      if (component.id === componentId) {
        return parent
      }
      if (component.children) {
        const foundParent = this.findParentComponent(componentId, component.children, component)
        if (foundParent !== undefined) {
          return foundParent
        }
      }
    }
    return undefined
  }

  /**
   * The Report Data is a collection of sections and subsections
   * which contain filters and charts to each one of them.
   * This will load only filters available to the current SECTION.
   * It shouldn't change on subsections any deeper;
   * It must change when the scroll reaches a different section;
   */
  get filtersAvailableAtReportSectionId() {
    let currentId = this.currentSectionId

    while (currentId) {
      const filters = this.filtersAvailable[currentId]
      if (filters && filters.length > 0) {
        return filters
      }

      const parent = this.findParentComponent(currentId)
      if (!parent) {
        break
      }

      currentId = parent.id
    }

    return []
  }

  get allFilters() {
    // Flatten the filtersAvailable object into a single array
    let flattenedMap = [...Object.values(this.filtersAvailable)].flat()
    // Filter the flattened array to include only unique filters by name
    const returnObj = flattenedMap.filter((value, index, self) => {
      return index === self.findIndex(t => t.name === value.name)
    })

    return returnObj // Return the filtered array of unique filters
  }

  setCurrentSectionId = sectionId => {
    if (!sectionId) return
    this.currentSectionId = sectionId

    // Get all filters available in the current section
    const filtersAvailable = this.filtersAvailable[sectionId]
    if (!filtersAvailable) return

    // Get only the filters that have been selected and are available in the current section
    const currentFilterSelection = [...this.filterOptionsSelected.entries()]
      .filter(([key, value]) =>
        filtersAvailable.find(filter => filter.name === key)
      )
      .map(([key, value]) => ({ key, value }))

    // Replace filterOptionsSelected with currentFilterSelection
    currentFilterSelection.forEach(({ key, value }) => {
      this.filterOptionsSelected.set(key, value)
    })

    // Add the new filters default values to the current section
    filtersAvailable.forEach(filter => {
      if (currentFilterSelection.find(item => item.key === filter.name)) {
        return
      }
      const defaultOption = filter.options.find(option => option.default)
      if (defaultOption) {
        this.setFilterOptionsSelected({
          filterName: filter.name,
          option: {
            id: defaultOption.value,
            value: defaultOption.value,
            label: defaultOption.text,
          },
        })
      }
    })
  }

  // Method to set filter options selected
  setFilterOptionsSelected = ({ filterName, option }) => {
    this.filterOptionsSelected.set(filterName, option)
  }

  // Wipe the filterOptionsSelected map
  invalidateFilterOptionsSelected = () => {
    this.filterOptionsSelected.clear()
  }

  setCurrentReport = report => {
    this.currentReport = report
    this.reportId = camelCase(report.id)
  }

  get reportListIds() {
    if (!this.userPreferences[this.reportId]) {
      this.userPreferences[this.reportId] = {
        reportListIds: [],
      }
    }

    return this.userPreferences[this.reportId].reportListIds
  }

  fetchUserPreferences = async () => {
    try {
      this.loadingUserPreferences = true
      this.userPreferences = await fetchUserPreferences()
    } catch (error) {
      this.error = error
    } finally {
      this.loadingUserPreferences = false
    }
  }

  /**
   * It saves the journey order such as (1, 2, 3)
   * @param journeyOrder - {reportId, sectionIds: []}
   * @returns {Promise<{}>}
   */
  saveUserPreferences = async ({ reportId, sectionIds }) => {
    try {
      runInAction(() => {
        this.errorSaveUserPreferences = false
        this.loadingUserPreferences = true
      })

      await postUserLogin()
      const userPreferences = {
        ...this.userPreferences,
        [reportId]: {
          ...this.userPreferences[reportId],
          sectionIds: [...sectionIds],
        },
      }
      await putUserPreferences(userPreferences)

      runInAction(() => {
        this.userPreferences = userPreferences
      })
    } catch (error) {
      runInAction(() => {
        this.errorSaveUserPreferences = error.message
      })
    } finally {
      runInAction(() => {
        this.loadingUserPreferences = false
      })
    }
  }
}

export default ReportStore
